import React, { useCallback, useEffect, useMemo } from 'react';
import globalWindow from 'global';
import { useAtomValue, useSetAtom } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import type { AnnotationLabelT } from '../../../../../common/types/types';
import { useLogger } from '../../../../../modernization-common/hooks/use-logger';
import { annotationLabelsAtom } from '../../../../labelling-v2/atoms/annotation-atoms';
import { EVENTS } from '../../../../labelling-v2/atoms/label-store-dispatch/events';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { ANNOTATION_TYPE, SEGMENTATION_ALPHA, SEGMENTATION_TOOLS, TOOL } from '../../../constants';
import useCanvas from '../../../providers/hooks/use-canvas';
import useStage from '../../../providers/hooks/use-stage';
import type { BrushAnnotationT } from '../../../types';
import { currentFrameAnnotationBitmapsAtom } from '../atoms/segmentation-atoms';
import Brush from '../brush/brush';
import SegmentationPolygon from '../brush/segmentation-polygon';
import SegmentationSelectionOverlay from './segmentation-selection-overlay';
import { applyErasedBitmapMask, applyOpacityToImageData, buildIdBuffer, clearCanvas, drawBitmap, drawSelectionOutlines, eraseMaskFromOtherAnnotations, invalidateOutlineCache, mergeAnnotationBitmap, removeAndExtractErasedRegions } from './utils';
type PropsT = {
  semanticAnnotations?: {
    [key: string]: BrushAnnotationT;
  };
};
const CanvasEditorV2 = ({
  semanticAnnotations = {}
}: PropsT) => {
  const {
    addAnnotation,
    updateAnnotation
  } = useStage();
  const {
    getCanvasContext,
    getDrawingCanvasContext
  } = useCanvas();
  const {
    brushToolSettingsAtom,
    selectedToolAtom,
    selectedAnnotationLabelAtom,
    currentFrameAtom,
    currentFrameDataAtom,
    dispatchAtom,
    selectedAnnotationIdsAtom,
    annotationsUnderReviewBitmapsAtom
  } = useStageAtoms();
  const currentFrame = useAtomValue(currentFrameAtom);
  const currentFrameData = useAtomValue(currentFrameDataAtom);
  const dispatch = useSetAtom(dispatchAtom);
  const selectAnnotation = useCallback((id: string | null) => {
    dispatch({
      type: EVENTS.SELECT_ANNOTATION,
      annotationId: id
    });
  }, [dispatch]);
  const selectedTool = useAtomValue(selectedToolAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const segmentationActive = selectedTool === TOOL.SEGMENTATION;
  const {
    selectedTool: selectedBrushTool
  } = useAtomValue(brushToolSettingsAtom);
  const {
    end,
    start
  } = useActionDuration();
  const logger = useLogger();
  const isSegmentationDrawingActive = segmentationActive && selectedBrushTool !== SEGMENTATION_TOOLS.SAM_BOUNDING_BOX && selectedBrushTool !== SEGMENTATION_TOOLS.SAM_CLICK && selectedBrushTool !== SEGMENTATION_TOOLS.AUTO_DETECT;
  const annotationBitmap = useAtomValue(currentFrameAnnotationBitmapsAtom);
  const reviewBitmaps = useAtomValue(annotationsUnderReviewBitmapsAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const annotationPixelMap = useMemo(() => {
    const ctx = getCanvasContext();
    if (!ctx) return undefined;
    return buildIdBuffer(ctx.canvas.width, ctx.canvas.height, annotationBitmap);
  }, [annotationBitmap, getCanvasContext]);
  useEffect(() => {
    if (!segmentationActive) {
      const ctx = getCanvasContext();
      const drawingCanvasCtx = getDrawingCanvasContext();
      if (!annotationLabel || !ctx || !drawingCanvasCtx) return;
      clearCanvas(drawingCanvasCtx);
    }
  }, [segmentationActive, annotationLabel, getCanvasContext, getDrawingCanvasContext]);
  const draw = useCallback(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  (frameBitmaps: {
    [labelTitle: string]: ImageData;
  }, review: {
    [labelTitle: string]: ImageData;
  }, selectedIds: Array<string>, labels: AnnotationLabelT[], shouldClearCanvas = false) => {
    let animationFrameId: number;
    const canvasContext = getCanvasContext();
    const render = () => {
      if (canvasContext) {
        const {
          width,
          height
        } = canvasContext.canvas;
        if (!width || !height || isNaN(width) || isNaN(height)) {
          logger?.error(`[canvas-editor-v2] Skipping render: canvas dimensions are invalid (${width}x${height}).`);
          return;
        }
        let mergedBitmap = new ImageData(width, height);
        if (shouldClearCanvas) clearCanvas(canvasContext);

        // Draw regular annotations
        Object.entries(frameBitmaps).forEach(([title, bitmap]) => {
          mergedBitmap = mergeAnnotationBitmap(mergedBitmap, bitmap, title, labels);
        });

        // Draw annotations under review (already colored)
        Object.entries(review).forEach(([title, bitmap]) => {
          mergedBitmap = mergeAnnotationBitmap(mergedBitmap, bitmap, title, labels);
        });
        drawBitmap(canvasContext, mergedBitmap);
        drawSelectionOutlines(canvasContext, selectedIds, semanticAnnotations, frameBitmaps);
      }
    };
    animationFrameId = globalWindow.requestAnimationFrame(render);
    return animationFrameId;
  },
  /* getCanvasContext is a stable getter function
   semanticAnnotations, annotationLabels are intentionally not in deps
   They are captured at render time; the draw function is called with updated bitmaps when needed */
  [getCanvasContext]);
  useEffect(() => {
    const animationFrameId = draw(annotationBitmap, reviewBitmaps, selectedAnnotationIds, annotationLabels, true);
    return () => {
      if (animationFrameId) globalWindow.cancelAnimationFrame(animationFrameId);
    };

    /* draw is intentionally not in deps to prevent infinite re-renders
    This effect runs when bitmaps or selected IDs change, which triggers redraw */
  }, [annotationBitmap, reviewBitmaps, selectedAnnotationIds, annotationLabels]);
  const getDrawingAnnotation = () => {
    let annotationIdx = '-1';
    if (annotationLabel) {
      Object.keys(semanticAnnotations).forEach(annotationIndex => {
        if (semanticAnnotations?.[annotationIndex]?.annotationLabel?.title === annotationLabel?.title &&
        // Filter IDs from ML store as they are never part of drawing annotation
        currentFrameData?.annotationMLStore?.[annotationIndex] === undefined) {
          annotationIdx = annotationIndex;
        }
      });
    }
    return annotationIdx;
  };
  const onPaintStart = useCallback(() => {
    start();
  }, [start]);
  const onPaint = () => {
    const ctx = getCanvasContext();
    const drawingCanvasCtx = getDrawingCanvasContext();
    if (!annotationLabel || !ctx || !drawingCanvasCtx) return;
    const drawingAnnotationIndex = getDrawingAnnotation();
    const rawImageData = drawingCanvasCtx.getImageData(0, 0, drawingCanvasCtx.canvas.width, drawingCanvasCtx.canvas.height);

    // Apply 50% opacity as while drawing opacity is 100%
    const currentImageData = applyOpacityToImageData(rawImageData, SEGMENTATION_ALPHA);
    const {
      updatedImageData: updatedCurrentImageData,
      erasedImageData
    } = removeAndExtractErasedRegions(currentImageData);
    drawingCanvasCtx.putImageData(updatedCurrentImageData, 0, 0);
    const base64String = drawingCanvasCtx.canvas.toDataURL('image/png');
    const brushAnnotation: BrushAnnotationT = {
      annotationType: ANNOTATION_TYPE.SEGMENTATION,
      annotationLabel,
      data: {
        base64String,
        color: annotationLabel.style.strokeColor
      }
    };
    invalidateOutlineCache(annotationLabel.title);
    const metadata = {
      actionDuration: end()
    };
    drawingAnnotationIndex !== '-1' ? updateAnnotation(drawingAnnotationIndex, {
      ...semanticAnnotations[drawingAnnotationIndex],
      ...brushAnnotation
    }, metadata) : addAnnotation(brushAnnotation, metadata);
    const otherAnnotationsForErasure = Object.fromEntries(Object.entries(semanticAnnotations).filter(([idx]) => idx !== drawingAnnotationIndex && currentFrameData?.annotationMLStore?.[idx] === undefined));
    const erasureSourceBitmap: Record<string, ImageData> = Object.fromEntries(Object.entries(otherAnnotationsForErasure).map(([, annotation]) => {
      const labelTitle = annotation?.annotationLabel?.title || '';
      const existingImageData = annotationBitmap?.[labelTitle];
      return existingImageData ? ([labelTitle, applyErasedBitmapMask(erasedImageData, existingImageData)] as const) : undefined;
    }).filter((entry): entry is readonly [string, ImageData] => entry !== undefined));
    eraseMaskFromOtherAnnotations(updatedCurrentImageData, annotationLabel.style.strokeColor, otherAnnotationsForErasure, erasureSourceBitmap, drawingAnnotationIndex, updateAnnotation, metadata);
    clearCanvas(drawingCanvasCtx);
  };
  const getDrawingBrushAnnotation = () => {
    let annotationIdx = getDrawingAnnotation();
    const annotation = annotationIdx !== '-1' ? semanticAnnotations[annotationIdx] : null;
    const commonProps = {
      key: `annotation-${annotationIdx}`,
      isDrawing: true,
      annotation,
      annotationIdx,
      annotationBitmap: annotationBitmap?.[annotation?.annotationLabel?.title ?? ''],
      onPaintComplete: onPaint,
      onPaintStart: onPaintStart
    };
    if (selectedBrushTool === SEGMENTATION_TOOLS.SEGMENTATION_POLYGON) {
      return <SegmentationPolygon {...commonProps} />;
    }
    return <Brush {...commonProps} />;
  };
  return <>
      {isSegmentationDrawingActive && getDrawingBrushAnnotation()}
      <SegmentationSelectionOverlay currentFrame={currentFrame} annotationBitmap={annotationBitmap} annotationPixelMap={annotationPixelMap} semanticAnnotations={semanticAnnotations} selectedTool={selectedTool} selectAnnotation={selectAnnotation} updateAnnotation={updateAnnotation} />
    </>;
};
export default CanvasEditorV2;